// ---------------------------------------------------------------------------
// features.js — the single wiring point for feature modules.
//
// The foundation (DesignEditor class) builds the imperative `editor` API object
// (see the big header comment in DesignEditor.js) and calls `registerFeatures`
// exactly once, in componentDidMount, AFTER the canvas + viewport + history are
// live and BEFORE the first render settles. Feature agents (layers, filters,
// crop, guides/rulers, text, shapes, bg-removal refine, export dialog, shortcuts)
// import their own modules here and call the registration seams:
//
//   editor.addToolButton({ id, title, icon, onClick, isActive?, group? })
//   editor.addPanelSection({ id, title, when(sel), render(editor) , order? })
//   editor.registerCommand(keySpec, fn, { when? })   // keySpec e.g. 'mod+g'
//   editor.enterMode(name, handlers) / exitMode(name) / isMode(name)
//
// Each feature owns its own file(s); this stays a thin dispatcher. Keep it a
// no-op until features land so the editor works standalone.
// ---------------------------------------------------------------------------

import { registerLayersPanel } from './LayersPanel';
import { registerImageAdjustments } from './imageAdjustments';
import { registerTextTools } from './textTools';
import { registerShapes } from './shapes';
import { registerCropMode } from './cropMode';
import { registerGuidesAndRulers } from './guides';
import { registerMaskRefine } from './maskRefine';
import { registerExportDialog } from './exportDialog';
import { registerShortcutsHelp } from './shortcutsHelp';

export function registerFeatures(editor) {
  // Feature agents: import and call your registrations here, e.g.
  registerLayersPanel(editor);
  // Spec B — image filters / transform / border panel.
  registerImageAdjustments(editor);
  // Spec E — text style upgrades + Textbox inserter.
  registerTextTools(editor);
  // Spec F — shapes/elements + SVG import + free-draw brush UI.
  registerShapes(editor);
  // Spec C — advanced crop mode (owns the 'crop' mode + double-click-to-crop).
  registerCropMode(editor);
  // Spec D — smart alignment guides + rulers + draggable guides.
  registerGuidesAndRulers(editor);
  // Spec G — bg-removal mask-refinement brush ('mask' mode + dialog).
  registerMaskRefine(editor);
  // Spec H — export dialog (format / quality / size / background).
  registerExportDialog(editor);
  // Spec H — keyboard shortcuts help ("?") + Alt-drag duplicate.
  registerShortcutsHelp(editor);
}
